import React, { useState } from "react";
import { Icon } from "@iconify-icon/react";
import { motion, AnimatePresence } from "framer-motion";
import { EventModal } from "../components/eventModal";
import { useEvents } from "../hooks/useEvent";
import { convertDate, convertTime } from "../helper";
import { Empty, Spin } from "antd";
import { FaRegImage } from "react-icons/fa";
import {usePageHeaderByKey} from "../hooks/usePageHeaderBykey";
import PublicPageShell from "../components/PublicPageShell";
export const Events = () => {
  const { pageHeader } = usePageHeaderByKey("events");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [direction, setDirection] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [visibleCount, setVisibleCount] = useState(6); // Show 6 initially
  const { events, loading } = useEvents();

  const showMore = () => {
    setVisibleCount((prev) => prev + 4); // Load 4 more
  };

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? "100%" : "-100%" }),
    center: { x: 0, opacity: 1, transition: { duration: 0.5 } },
    exit: (direction) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
      transition: { duration: 0.5 },
    }),
  };

  const showImage = (newIndex, newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prev) =>
      newIndex < 0
        ? events.length - 1
        : newIndex >= events.length
        ? 0
        : newIndex
    );
  };

  const showEventDetails = (event) => {
    setSelectedEvent(event);
    setVisible(true);
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Empty description="No events found" />
      </div>
    );
  }

  return (
    <div>
      {/* Event Image Slider */}
      <div className="relative w-full h-[30rem] sm:h-[40rem] overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            className="absolute inset-0 w-full h-full flex flex-col justify-end "
            variants={slideVariants}
            initial="center"
            animate="center"
            exit="exit"
            custom={direction}
          >
            {events[currentIndex]?.images?.[0]?.image_url ? (
              <img
                src={events[currentIndex].images[0].image_url}
                alt={`Event ${currentIndex + 1}`}
                className="w-full h-full object-contian"
                style={{
                  imageRendering: "auto", // Use "auto" for photos
                }}
                loading="lazy"
                // If you have multiple resolutions, add srcSet and sizes here:
                // srcSet={`${events[currentIndex].images[0].image_url}?w=800 800w, ${events[currentIndex].images[0].image_url}?w=1600 1600w`}
                // sizes="(max-width: 1024px) 100vw, 1024px"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                <span className="text-gray-500 text-xl font-semibold">
                  No Image Available
                </span>
              </div>
            )}
            <div className="absolute bottom-0 w-full bg-gradient-to-t from-black/60 to-transparent text-white p-2 sm:p-4">
              <h2 className="text-lg sm:text-xl font-semibold text-white capitalize">
                {events[currentIndex]?.title}
              </h2>
              <p
                className="text-xs sm:text-sm"
                dangerouslySetInnerHTML={{
                  __html:
                    events[currentIndex]?.description &&
                    events[currentIndex].description.length > 100
                      ? events[currentIndex].description.slice(0, 100) + "..."
                      : events[currentIndex]?.description,
                }}
              ></p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs sm:text-sm">
                  {convertDate(events[currentIndex]?.date)}
                </p>
                <p className="text-xs sm:text-sm">
                  {convertTime(events[currentIndex]?.time)}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between absolute top-1/2 transform -translate-y-1/2 w-full px-2 sm:px-5">
          <button
            onClick={() => showImage(currentIndex - 1, -1)}
            className="px-2 sm:px-3 py-1 bg-black/40 hover:bg-black/70 text-white transition rounded-full"
          >
            <Icon icon="ep:arrow-left-bold" width="20" height="40" />
          </button>
          <button
            onClick={() => showImage(currentIndex + 1, 1)}
            className="px-2 sm:px-3 py-1 bg-black/40 hover:bg-black/70 text-white transition rounded-full"
          >
            <Icon icon="weui:arrow-filled" width="20" height="40" />
          </button>
        </div>
      </div>

      {/* Events List */}
      <PublicPageShell className="py-6 flex flex-col items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-center">
          {pageHeader?.h1 || "Upcoming Events"}
        </h1>
        <p className="mt-2 sm:mt-4 text-base sm:text-lg text-center">
          {pageHeader?.tagline || "In the community and in the area"}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full">
          {events.slice(0, visibleCount).map((event, index) => (
            <motion.div
              key={event.id}
              className="flex flex-col sm:flex-row gap-4 p-2 sm:p-4 "
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              {event.images?.[0]?.image_url ? (
                <img
                  src={event.images[0].image_url}
                  alt={event.title}
                  className="w-full sm:w-40 h-42 sm:h-52 rounded-lg object-cover"
                />
              ) : (
                <div className="w-full sm:w-40 h-48 sm:h-56 rounded-lg bg-gray-200 flex flex-col items-center justify-center">
                  <FaRegImage className="text-4xl text-gray-400 mb-2" />
                  <span className="text-gray-500 text-xs font-semibold">
                    No Image
                  </span>
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-base sm:text-sm md:text-base font-semibold">
                  {event.title}
                </h2>
                <p className="text-gray-500 mt-2 text-xs sm:text-sm">
                  On {convertDate(event.date)} at {convertTime(event.time)},{" "}
                </p>
                <p className="text-gray-500 text-xs sm:text-sm leading-tight">
                  <strong>
                    {(() => {
                      const line1 = [
                        event.street_number,
                        event.compass_direction,
                        event.street_name,
                        event.post_street_direction,
                        event.unit_number ? `Unit ${event.unit_number}` : null,
                      ]
                        .filter(Boolean)
                        .join(" ");
                      const line2 = [
                        event.county,
                        event.state,
                        event.country,
                        event.zip_code,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      if (!line1 && !line2) return "No address available";
                      return (
                        <>
                          {line1}
                          {line2 && (
                            <>
                              <br />
                              {line2}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </strong>
                </p>
                <p
                  className="text-gray-400 text-xs sm:text-sm mt-2"
                  dangerouslySetInnerHTML={{
                    __html:
                      event.description && event.description.length > 30
                        ? event.description.slice(0, 30) + "..."
                        : event.description,
                  }}
                ></p>
                <button
                  onClick={() => showEventDetails(event)}
                  className="mt-4 border px-4 py-1 rounded text-gray-500 hover:bg-red-600 hover:text-white w-full sm:w-auto"
                >
                  CLICK HERE FOR MORE DETAILS
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View More Button */}
        {visibleCount < events.length && (
          <motion.button
            onClick={showMore}
            className="mt-6 bg-red-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-md hover:bg-red-700 transition w-full sm:w-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View More
          </motion.button>
        )}
      </PublicPageShell>

      {/* Event Modal */}
      {visible && (
        <EventModal
          visible={visible}
          onClose={() => setVisible(false)}
          data={selectedEvent}
        />
      )}
    </div>
  );
};
